import { useEffect } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';
import { Link } from 'react-router-dom';
import JsonLd from '../../../components/base/JsonLd';

export default function TransitionalCare() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/services/transitional-care#webpage`,
    url: `${siteUrl}/services/transitional-care`,
    name: 'Transitional Care Services Las Vegas | Celebrity Home Health',
    description: 'Hospital-to-home transitional care in Las Vegas — coordinated care transitions, 24/7 support, and follow-up to prevent readmissions. Medicare certified, ACHC accredited.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    medicalSpecialty: 'ContinuityOfCare',
    lastReviewed: '2026-04-11',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: 'Transitional Care', item: `${siteUrl}/services/transitional-care` },
      ],
    },
  };

  useEffect(() => {
    document.title = 'Transitional Care Las Vegas | Hospital to Home Care Services';
  }, []);

  const transitionPrograms = [
    {
      icon: 'ri-hospital-line',
      title: 'Reduce Hospital Readmissions',
      description: 'Comprehensive transition program designed to prevent unnecessary hospital readmissions through proactive monitoring and early intervention.',
      image: 'https://public.readdy.ai/ai/img_res/8845b6da75effc948fd779a2624d2a3a.jpg',
      benefits: [
        'Post-discharge assessment within 24-48 hours',
        'Medication reconciliation and management',
        'Symptom monitoring and early intervention',
        'Communication with hospital discharge team',
        'Follow-up appointment coordination',
        'Emergency response protocols',
        'Family caregiver education and support',
        'Risk factor identification and management'
      ],
      timeline: '30-60 days post-discharge',
      focus: 'Preventing readmissions, ensuring safe transition'
    },
    {
      icon: 'ri-medicine-bottle-line',
      title: 'Medication Reconciliation at Home',
      description: 'Comprehensive medication review and management program to prevent drug interactions and ensure proper medication adherence.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20pharmacist%20and%20nurse%20reviewing%20medications%20with%20elderly%20patient%20in%20bright%20modern%20home%20kitchen%2C%20medication%20reconciliation%20at%20home%2C%20clean%20white%20interior%20with%20organized%20medication%20setup%2C%20healthcare%20team%20ensuring%20medication%20safety%20in%20residential%20setting&width=500&height=350&seq=medication-reconciliation-lv&orientation=landscape',
      benefits: [
        'Complete medication review and reconciliation',
        'Drug interaction screening and prevention',
        'Medication adherence monitoring',
        'Pill organization and scheduling systems',
        'Side effect monitoring and management',
        'Communication with prescribing physicians',
        'Patient and family medication education',
        'Cost-effective medication alternatives'
      ],
      timeline: 'Ongoing throughout care period',
      focus: 'Medication safety, adherence, optimization'
    },
    {
      icon: 'ri-team-line',
      title: 'Care Coordination Services',
      description: 'Seamless coordination between hospital, home health team, physicians, and specialists to ensure continuity of care.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20healthcare%20team%20coordinating%20care%20for%20patient%20in%20comfortable%20Las%20Vegas%20home%2C%20care%20coordination%20meeting%20with%20multiple%20healthcare%20providers%2C%20clean%20white%20interior%20with%20medical%20charts%20and%20communication%20devices%2C%20team%20collaboration%20in%20residential%20setting&width=500&height=350&seq=care-coordination-lv&orientation=landscape',
      benefits: [
        'Communication with all healthcare providers',
        'Appointment scheduling and coordination',
        'Medical record management and sharing',
        'Insurance authorization and verification',
        'Specialist referral coordination',
        'Diagnostic test scheduling and follow-up',
        'Treatment plan implementation',
        'Progress reporting to physicians'
      ],
      timeline: 'Throughout entire care episode',
      focus: 'Seamless communication, coordinated care'
    },
    {
      icon: 'ri-parent-line',
      title: 'Family Education and Support',
      description: 'Comprehensive education program for family caregivers to ensure safe and effective care continuation at home.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20nurse%20teaching%20family%20members%20how%20to%20provide%20care%20for%20elderly%20patient%20in%20comfortable%20Las%20Vegas%20home%20living%20room%2C%20family%20caregiver%20education%20at%20home%2C%20clean%20white%20interior%20with%20educational%20materials%2C%20nurse%20training%20family%20in%20residential%20setting&width=500&height=350&seq=family-education-lv&orientation=landscape',
      benefits: [
        'Caregiver training and education',
        'Emergency response training',
        'Medication administration instruction',
        'Symptom recognition and reporting',
        'Equipment use and maintenance training',
        'Safety protocols and fall prevention',
        'Stress management and support resources',
        '24/7 support hotline access'
      ],
      timeline: 'Initial intensive training, ongoing support',
      focus: 'Empowering families, ensuring safety'
    }
  ];

  const transitionSteps = [
    {
      step: '1',
      title: 'Pre-Discharge Planning',
      description: 'Coordination with hospital discharge team to plan seamless transition home.',
      icon: 'ri-calendar-check-line'
    },
    {
      step: '2',
      title: 'Home Readiness Assessment',
      description: 'Evaluation of home environment and preparation for safe patient return.',
      icon: 'ri-home-heart-line'
    },
    {
      step: '3',
      title: 'Initial Home Visit',
      description: 'Comprehensive assessment within 24-48 hours of hospital discharge.',
      icon: 'ri-user-heart-line'
    },
    {
      step: '4',
      title: 'Care Plan Implementation',
      description: 'Execution of personalized care plan with regular monitoring and adjustments.',
      icon: 'ri-file-list-3-line'
    },
    {
      step: '5',
      title: 'Ongoing Monitoring',
      description: 'Continuous assessment and communication with healthcare team.',
      icon: 'ri-eye-line'
    },
    {
      step: '6',
      title: 'Successful Transition',
      description: 'Achievement of care goals and successful transition to independence or ongoing care.',
      icon: 'ri-trophy-line'
    }
  ];

  const riskFactors = [
    'Recent hospitalization (within 30 days)',
    'Multiple chronic conditions',
    'Complex medication regimens',
    'History of hospital readmissions',
    'Limited family support or social isolation',
    'Cognitive impairment or confusion',
    'Functional limitations or mobility issues',
    'Inadequate discharge planning',
    'Poor medication adherence',
    'Lack of follow-up care coordination'
  ];

  const outcomes = [
    {
      metric: '75%',
      description: 'Reduction in 30-day readmissions',
      icon: 'ri-hospital-line'
    },
    {
      metric: '90%',
      description: 'Patient satisfaction rate',
      icon: 'ri-user-smile-line'
    },
    {
      metric: '85%',
      description: 'Medication adherence improvement',
      icon: 'ri-medicine-bottle-line'
    },
    {
      metric: '95%',
      description: 'Successful care transitions',
      icon: 'ri-check-double-line'
    }
  ];

  return (
    <>
      <div className="bg-white">
        <JsonLd data={jsonLd as Record<string, unknown>} id="json-ld-transitional-care" />
        <Header />
        
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-300/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-6xl font-bold mb-6">Transitional Care / Hospital-to-Home</h1>
                <p className="text-xl text-white/90 leading-relaxed mb-8">
                  Seamless <strong>hospital-to-home transition services</strong> in Las Vegas designed to reduce 
                  readmissions and ensure safe, successful recovery. Professional care coordination, medication 
                  management, and family education across Henderson, Summerlin, and North Las Vegas.
                </p>
                <div className="bg-teal-900/30 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-teal-700/30">
                  <h3 className="text-xl font-bold mb-3">Transition Services:</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-teal-100">
                    <span>• Reduce hospital readmissions</span>
                    <span>• Medication reconciliation</span>
                    <span>• Care coordination</span>
                    <span>• Family education</span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button size="lg" className="w-full sm:w-auto bg-white text-teal-600 hover:bg-gray-50 shadow-2xl whitespace-nowrap">
                      Start Transitional Care
                    </Button>
                  </Link>
                  <a href="tel:866-939-0330">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-teal-600 shadow-2xl whitespace-nowrap">
                      Call: 866-939-0330
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20team%20facilitating%20smooth%20hospital%20to%20home%20transition%20in%20Las%20Vegas%20residence%2C%20transitional%20care%20coordination%20with%20patient%20and%20family%2C%20comfortable%20residential%20setting%20with%20medical%20equipment%20and%20care%20coordination%20materials&width=500&height=400&seq=transitional-care-hero-lv&orientation=landscape"
                  alt="Transitional Care Hospital to Home Las Vegas - Care Coordination"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Transitional Care Programs */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Transitional Care Programs</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized <strong>hospital-to-home transition services</strong> designed to ensure safe, 
                successful recovery and prevent readmissions for Las Vegas patients.
              </p>
            </div>

            <div className="space-y-20">
              {transitionPrograms.map((program, index) => (
                <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mr-6 shadow-lg">
                        <i className={`${program.icon} text-white text-3xl`}></i>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900">{program.title}</h3>
                    </div>
                    <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                      {program.description}
                    </p>
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Program Benefits:</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {program.benefits.map((benefit, benefitIndex) => (
                          <div key={benefitIndex} className="flex items-center bg-teal-50 rounded-lg p-3 border border-teal-100/50">
                            <i className="ri-check-line text-teal-600 mr-3 text-lg"></i>
                            <span className="text-gray-700 font-medium">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100/50">
                        <h5 className="font-bold text-gray-900 mb-2">Timeline</h5>
                        <p className="text-teal-700 font-semibold">{program.timeline}</p>
                      </div>
                      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100/50">
                        <h5 className="font-bold text-gray-900 mb-2">Primary Focus</h5>
                        <p className="text-teal-700 font-semibold">{program.focus}</p>
                      </div>
                    </div>
                  </div>
                  <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                    <div className="relative">
                      <img
                        src={program.image}
                        alt={`${program.title} - Transitional Care Las Vegas`}
                        className="rounded-2xl shadow-2xl object-cover w-full h-96"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Transition Process */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Our Hospital-to-Home Transition Process
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                A systematic approach to ensure safe and successful transition from hospital to home
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {transitionSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-xl border border-teal-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center shadow-2xl">
                        <span className="text-white font-bold text-xl">{step.step}</span>
                      </div>
                      <div className="ml-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                          <i className={`${step.icon} text-white text-xl`}></i>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-700 text-lg leading-relaxed">{step.description}</p>
                  </div>
                  {index < transitionSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <i className="ri-arrow-right-line text-teal-300 text-2xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Risk Factors */}
        <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Readmission Risk Factors We Address
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Common factors that increase hospital readmission risk and how our program addresses them
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
              {riskFactors.map((factor, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-teal-200">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center mr-4 shadow-md">
                      <i className="ri-alert-line text-white text-sm"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{factor}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Outcomes Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Proven Transitional Care Outcomes
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our transitional care program delivers measurable results for patients and families
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {outcomes.map((outcome, index) => (
                <div key={index} className="text-center group">
                  <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <i className={`${outcome.icon} text-white text-4xl`}></i>
                  </div>
                  <div className="text-5xl font-bold text-teal-600 mb-4">{outcome.metric}</div>
                  <p className="text-gray-600 text-lg leading-relaxed font-medium">{outcome.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Benefits of Professional Transitional Care
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Why professional hospital-to-home transition services make a difference
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-shield-check-line text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Safer Transitions</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Professional oversight ensures safe transition from hospital to home with reduced complications.
                </p>
              </div>
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-team-line text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Coordinated Care</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Seamless communication between all healthcare providers ensures continuity of care.
                </p>
              </div>
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-xl group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-heart-line text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Peace of Mind</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Families feel confident knowing professional support is available during the transition period.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-gray-900 via-teal-900/30 to-gray-900 text-white relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-5xl font-bold mb-8">
                  Ready for Safe Hospital-to-Home Transition?
                </h2>
                <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                  Contact Celebrity Home Health today to learn how our comprehensive <strong>transitional care programs</strong> 
                  can ensure a safe, successful transition from hospital to your Las Vegas home.
                </p>
                <div className="flex flex-col sm:flex-row gap-6">
                  <Link to="/contact">
                    <Button size="lg" className="w-full sm:w-auto bg-white text-teal-600 hover:bg-gray-50 shadow-2xl whitespace-nowrap">
                      Start Transitional Care
                    </Button>
                  </Link>
                  <a href="tel:866-939-0330">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-teal-600 shadow-2xl whitespace-nowrap">
                      Call: 866-939-0330
                    </Button>
                  </a>
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-3xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=Happy%20patient%20successfully%20transitioned%20from%20hospital%20to%20home%20with%20professional%20healthcare%20team%20in%20Las%20Vegas%2C%20successful%20hospital%20to%20home%20transition%2C%20patient%20and%20family%20celebrating%20safe%20return%20home%20with%20care%20coordination%20team%20in%20comfortable%20residential%20setting&width=500&height=400&seq=cta-transition-success-lv&orientation=landscape"
                    alt="Successful Hospital to Home Transition - Las Vegas Transitional Care"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
